/* ──────────────────────────────────────────────────────────
   BankLensContext.jsx — React context that fetches real
   data from the BankLens API and exposes the same helpers
   that views previously consumed from banksData.js.
   Falls back to static seed data while loading or on error.
   ────────────────────────────────────────────────────────── */
import React, {
    createContext, useContext, useEffect, useState, useMemo, useCallback,
} from 'react';
import { fetchOverview, fetchBankDetail } from './bankLensApi';
import {
    BANKS as STATIC_BANKS,
    BENCHMARKS, METRIC_INFO,
    CRR_BY_YEAR, SLR_BY_YEAR,
    getMetrics as staticGetMetrics,
    getLatestMetrics as staticGetLatestMetrics,
    getLiquidityData as staticGetLiquidityData,
    fmtMktCap, fmtCr, getStatus, getTotalMarketCap,
} from './banksData';

const BankLensContext = createContext(null);

/* ── Provider ─────────────────────────────────────────── */
export function BankLensProvider({ children }) {
    const [overview, setOverview] = useState(null);
    const [detailCache, setDetailCache] = useState({});   // { ticker: payload }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /* Load overview on mount */
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchOverview()
            .then(data => { if (!cancelled) setOverview(data); })
            .catch(err => { if (!cancelled) setError(err); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    /* Fetch single bank detail (cached) */
    const loadDetail = useCallback(async (ticker) => {
        if (detailCache[ticker]) return detailCache[ticker];
        try {
            const data = await fetchBankDetail(ticker);
            setDetailCache(prev => ({ ...prev, [ticker]: data }));
            return data;
        } catch (err) {
            console.error('Failed to load bank detail', ticker, err);
            return null;
        }
    }, [detailCache]);

    /* ── Derived helpers that mirror banksData.js API ──── */

    /** Bank metadata list — API shape uses snake_case; map to camelCase for UI */
    const banks = useMemo(() => {
        if (!overview?.banks?.length) return STATIC_BANKS;
        return overview.banks.map(b => ({
            ticker: b.ticker,
            name: b.name,
            shortName: b.short_name ?? STATIC_BANKS.find(s => s.ticker === b.ticker)?.shortName ?? b.name,
            type: b.bank_type ?? STATIC_BANKS.find(s => s.ticker === b.ticker)?.type ?? 'PVT',
            color: b.color ?? STATIC_BANKS.find(s => s.ticker === b.ticker)?.color ?? '#888',
            // raw merged flat object for quick metric access
            _raw: b,
        }));
    }, [overview]);

    /** Latest metrics for a ticker (from overview payload) */
    const getLatestMetrics = useCallback((ticker) => {
        if (!overview?.banks?.length) return staticGetLatestMetrics(ticker);
        const raw = overview.banks.find(b => b.ticker === ticker);
        if (!raw) return staticGetLatestMetrics(ticker);
        return {
            ticker,
            y: raw.fiscal_year,
            nim: raw.nim, roa: raw.roa, roe: raw.roe, cti: raw.cti,
            gnpa: raw.gnpa, nnpa: raw.nnpa, pcr: raw.pcr,
            car: raw.car, cet1: raw.cet1, ldr: raw.ldr,
            rev: raw.rev, pat: raw.pat,
            intInc: raw.intInc, intExp: raw.intExp,
            ta: raw.ta, eq: raw.eq,
            price: raw.price, change: raw.change, changePct: raw.changePct,
            marketCap: raw.marketCap, pe: raw.pe, eps: raw.eps,
            yearLow: raw.yearLow, yearHigh: raw.yearHigh,
        };
    }, [overview]);

    /** Full history for a ticker: try detail cache, else static fallback */
    const getMetrics = useCallback((ticker) => {
        const detail = detailCache[ticker];
        if (detail?.history?.length) {
            return detail.history.map(h => ({
                y: h.fiscal_year,
                nim: h.nim, roa: h.roa, roe: h.roe, cti: h.cti,
                gnpa: h.gnpa, nnpa: h.nnpa, pcr: h.pcr,
                car: h.car, cet1: h.cet1, ldr: h.ldr,
                rev: h.rev, pat: h.pat,
                intInc: h.intInc, intExp: h.intExp,
                ta: h.ta, eq: h.eq,
            }));
        }
        return staticGetMetrics(ticker);
    }, [detailCache]);

    /** Liquidity helper */
    const getLiquidityData = useCallback((ticker) => {
        const detail = detailCache[ticker];
        if (detail?.history?.length) {
            const rates = detail.policyRates || {};
            const crrMap = rates.crr || CRR_BY_YEAR;
            const slrMap = rates.slr || SLR_BY_YEAR;
            return detail.history.map(h => ({
                year: h.fiscal_year,
                ldr: h.ldr,
                crr: crrMap[h.fiscal_year] ?? 4.0,
                slr: slrMap[h.fiscal_year] ?? 18.0,
            }));
        }
        return staticGetLiquidityData(ticker);
    }, [detailCache]);

    /** Quotes map (from overview) */
    const currentQuotes = useMemo(() => {
        if (!overview?.banks?.length) return {};
        const m = {};
        overview.banks.forEach(b => {
            m[b.ticker] = {
                price: b.price, change: b.change, changePct: b.changePct,
                marketCap: b.marketCap, pe: b.pe, eps: b.eps,
                yearLow: b.yearLow, yearHigh: b.yearHigh,
            };
        });
        return m;
    }, [overview]);

    /** Summary stats */
    const summary = useMemo(() => {
        if (overview?.summary) {
            return {
                totalMarketCap: overview.summary.totalMarketCapCr,
                avgROE: overview.summary.avgRoe,
                avgGNPA: overview.summary.avgGnpa,
                trackedBanks: overview.summary.trackedBanks,
            };
        }
        return {
            totalMarketCap: getTotalMarketCap(),
            avgROE: 0,
            avgGNPA: 0,
            trackedBanks: STATIC_BANKS.length,
        };
    }, [overview]);

    const value = useMemo(() => ({
        loading, error, banks, summary, currentQuotes,
        getLatestMetrics, getMetrics, getLiquidityData, loadDetail,
        // re-export pure constants unchanged
        BENCHMARKS, METRIC_INFO,
        fmtMktCap, fmtCr, getStatus,
    }), [loading, error, banks, summary, currentQuotes,
        getLatestMetrics, getMetrics, getLiquidityData, loadDetail]);

    return (
        <BankLensContext.Provider value={value}>
            {children}
        </BankLensContext.Provider>
    );
}

export function useBankLens() {
    const ctx = useContext(BankLensContext);
    if (!ctx) throw new Error('useBankLens must be used within <BankLensProvider>');
    return ctx;
}
